import type { OcpiLocation } from "../types/ocpi";

export interface NetworkStats {
  cities: number;
  locations: number;
  acSockets: number;
  dcSockets: number;
}

/**
 * Filter rule: drop records whose `city` is missing or a placeholder
 * (the upstream dataset contains a handful of L11x rows with `city: "1"`).
 * Pure and stable so the widget can be reused anywhere.
 */
export function isValidLocation(loc: OcpiLocation): boolean {
  if (!loc?.city) return false;
  const city = String(loc.city).trim();
  if (city.length < 2) return false;
  if (!loc.coordinates) return false;
  const lat = parseFloat(loc.coordinates.latitude);
  const lng = parseFloat(loc.coordinates.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return true;
}

function isDcConnector(powerType: string | undefined): boolean {
  return String(powerType ?? "").startsWith("DC");
}

/**
 * Compute network-level summary stats from a list of OCPI locations.
 * - cities: unique normalized city names (Turkish-locale lowercase)
 * - locations: unique geographical coordinate (5 decimal precision)
 * - acSockets / dcSockets: total connector counts by power type
 */
export function computeStats(locations: OcpiLocation[]): NetworkStats {
  const valid = locations.filter(isValidLocation);

  const cities = new Set<string>();
  const coords = new Set<string>();
  let ac = 0;
  let dc = 0;

  for (const loc of valid) {
    cities.add(loc.city.toLocaleLowerCase("tr-TR").trim());
    const lat = parseFloat(loc.coordinates.latitude).toFixed(5);
    const lng = parseFloat(loc.coordinates.longitude).toFixed(5);
    coords.add(`${lng},${lat}`);
    for (const evse of loc.evses ?? []) {
      for (const c of evse.connectors ?? []) {
        if (isDcConnector(c.power_type)) dc++;
        else ac++;
      }
    }
  }

  return {
    cities: cities.size,
    locations: coords.size,
    acSockets: ac,
    dcSockets: dc,
  };
}
